import { useEffect, useState } from 'react';

interface UseOnboardingAnalysisMessagesOptions {
  messages: readonly string[];
  intervalMs?: number;
  enabled?: boolean;
  /** Change to restart with a freshly shuffled message order. */
  resetKey?: string;
}

export interface OnboardingAnalysisMessageState {
  message: string;
  index: number;
}

function shuffleMessages(messages: readonly string[]): string[] {
  const shuffled = [...messages];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const current = shuffled[i]!;
    shuffled[i] = shuffled[j]!;
    shuffled[j] = current;
  }
  return shuffled;
}

export function useOnboardingAnalysisMessages({
  messages,
  intervalMs = 2200,
  enabled = true,
  resetKey,
}: UseOnboardingAnalysisMessagesOptions): OnboardingAnalysisMessageState {
  const [order, setOrder] = useState<string[]>(() => shuffleMessages(messages));
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setOrder(shuffleMessages(messages));
    setIndex(0);
  }, [messages, resetKey, enabled]);

  useEffect(() => {
    if (!enabled || order.length <= 1) {
      return;
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return;
    }

    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % order.length);
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [order, intervalMs, enabled, resetKey]);

  if (!enabled || order.length === 0) {
    return { message: messages[0] ?? '', index: 0 };
  }

  return { message: order[index] ?? order[0] ?? '', index };
}
